import { useState, useEffect } from "react";
import {
  UseQueryResult,
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

interface Watchlist {
  coinId: number;
  isActive: boolean;
  coingeckoId: string;
  name: string;
  symbol: string;
  iconUrl: string;
  price?: any;
}

interface WatchlistPage {
  items: Watchlist[];
  cursor?: string;
  remaining: number;
  total: number;
}

export const useWatchlistQueryKey = ["positions", "watchlist"] as const;
export const useWatchlistPaginatedQueryKey = ["positions", "watchlist", "paginated"] as const;

// New paginated watchlist hook
export const useWatchlistPaginated = () => {
  const { user, getToken } = useAuth();
  const email = user?.email;
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce searchQuery by 500ms
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Use different endpoints based on whether we're searching or not
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...useWatchlistPaginatedQueryKey, debouncedSearchQuery],
    queryFn: async ({ pageParam = undefined }) => {
      const token = await getToken();
      
      if (!debouncedSearchQuery) {
        // When not searching, use original endpoint to get user's watchlist
        const res = await authorizedAxios(token).get("/positions/watchlist");
        return {
          items: res.data,
          cursor: undefined, // No pagination for watchlist
          remaining: 0,
          total: res.data.length
        } as WatchlistPage;
      } else {
        // When searching, use paginated search endpoint
        const params = new URLSearchParams();
        params.append("search", debouncedSearchQuery);
        if (pageParam) params.append("cursor", pageParam);
        params.append("limit", "20");

        const res = await authorizedAxios(token).get(
          `/positions/watchlist/search?${params.toString()}`
        );
        return res.data as WatchlistPage;
      }
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled: !!email,
  });

  // Flatten all pages into single array
  const watchlist = data?.pages?.flatMap(page => page.items) || [];

  // Mutation for add/update/remove with optimistic updates
  const mutation = useMutation({
    mutationFn: async (payload: any) => {
      const token = await getToken();
      const res = await authorizedAxios(token).post("/positions/watchlist", payload);
      return res.data;
    },
    onMutate: async (newItem) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: useWatchlistPaginatedQueryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(useWatchlistPaginatedQueryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(
        [...useWatchlistPaginatedQueryKey, debouncedSearchQuery],
        (old: any) => {
          if (!old?.pages) return old;
          
          return {
            ...old,
            pages: old.pages.map((page: WatchlistPage) => ({
              ...page,
              items: page.items.map((item: Watchlist) => {
                if (item.coingeckoId === newItem.coingeckoId) {
                  return { ...item, isActive: newItem.isActive };
                }
                return item;
              }),
            })),
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, newItem, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(useWatchlistPaginatedQueryKey, context?.previousData);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: useWatchlistPaginatedQueryKey,
      });
    },
  });

  return {
    isQueryLoading: isLoading,
    error,
    watchlist,
    searchQuery,
    setSearchQuery,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isMutating: mutation.isPending,
    mutateWatchlist: mutation.mutate,
  };
};

// Original hook for backward compatibility
export const useWatchlist = () => {
  const { user, getToken } = useAuth();
  const email = user?.email;
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce searchQuery by 500ms
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Query watchlist
  const {
    isLoading: isQueryLoading,
    isIdle,
    isSuccess,
    error,
    isError,
    data,
    refetch,
  }: UseQueryResult<Watchlist[], unknown> = useQuery({
    queryKey: [...useWatchlistQueryKey, debouncedSearchQuery],
    queryFn: async () => {
      const token = await getToken();
      const res = await authorizedAxios(token).get(
        "/positions/watchlist" +
          (debouncedSearchQuery ? `?search=${debouncedSearchQuery}` : "")
      );
      return res.data;
    },
    enabled: !!email,
  });

  // Mutation for add/update/remove with optimistic updates  
  const mutation = useMutation({
    mutationFn: async (payload: any) => {
      const token = await getToken();
      const res = await authorizedAxios(token).post("/positions/watchlist", payload);
      return res.data;
    },
    onMutate: async (newItem) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: useWatchlistQueryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([...useWatchlistQueryKey, debouncedSearchQuery]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        [...useWatchlistQueryKey, debouncedSearchQuery],
        (old: Watchlist[] | undefined) => {
          if (!old) return old;
          
          return old.map((item) => {
            if (item.coingeckoId === newItem.coingeckoId) {
              return { ...item, isActive: newItem.isActive };
            }
            return item;
          });
        }
      );

      return { previousData };
    },
    onError: (err, newItem, context) => {
      // Roll back on error
      queryClient.setQueryData([...useWatchlistQueryKey, debouncedSearchQuery], context?.previousData);
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: useWatchlistQueryKey,
      });
    },
  });

  return {
    isQueryLoading,
    isIdle,
    error,
    watchlist: data,
    searchQuery,
    setSearchQuery,
    refetch,
    isMutating: mutation.isPending,
    mutateWatchlist: mutation.mutate,
  };
};
